import React, { useEffect, useState } from "react";
import { Col, Row, Space, Spin, Table, Typography, Tooltip, Modal, Button, Dropdown, message, notification } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { PlusOutlined, InfoCircleOutlined, MoreOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import Card from "antd/es/card/Card";
import { PrimaryButton } from "../shared/button/Buttons";
import { TestSet } from "../../model_gen/evaluation";
import { Link } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { NEW_TEST_SET_STORAGE_KEY } from "./TestSetNew";
import { IntentService } from "../../services/IntentService";
import { IntentGroup } from "../../model_gen/intent";

type TestSetColumns = ColumnsType<TestSet>;

const TestSets: React.FC = () => {
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams();
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);
  const [cachedTestSets, setCachedTestSets] = useState<Record<number, TestSet[]>>({});
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const navigate = useNavigate();


  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(Number(intentGroupId), false, setIsLoading, apiKey).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  const handleDeleteTestSet = (testSet: TestSet) => {
    Modal.confirm({
      title: "Delete Test Set",
      content: `Are you sure you want to delete test set "${testSet.name}"?`,
      okText: "Yes",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () => {
        TestSetService.deleteTestSet(testSet.id.toString(), null, apiKey).subscribe(
          () => {
            message.success("Test set deleted successfully");
            // Remove the deleted test set from the current page
            const updatedTestSets = testSets.filter(ts => ts.id !== testSet.id);
            setTestSets(updatedTestSets);
            // Update the cache
            setCachedTestSets(prev => ({
              ...prev,
              [currentPage]: updatedTestSets
            }));
            // Update total count
            setTotalCount(prev => prev - 1);
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: `Failed to delete test set: ${error}`,
            });
          }
        );
      },
    });
  };

  const testSetColumns: TestSetColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: false,
      width: "30%",
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {text}
        </div>
      ),
    },
    {
      title: (
        <Space>
          Golden
          <Tooltip title="Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning">
            <InfoCircleOutlined
              style={{
                color: "rgba(0, 0, 0, 0.45)",
                fontSize: "14px",
              }}
            />
          </Tooltip>
        </Space>
      ),
      dataIndex: "is_golden",
      key: "is_golden",
      width: "15%",
      render: (isGolden: boolean) => (
        <span>{isGolden ? "Yes" : "No"}</span>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (date) => moment(date).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "# Requests",
      dataIndex: ["meta", "request_count"],
      key: "request_count",
      width: "15%",
    },
    {
      title: "",
      key: "actions",
      width: "32px",
      render: (_, record: TestSet) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteTestSet(record);
                },
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 0 }}
          />
        </Dropdown>
      ),
    },
  ];

  const fetchTestSets = (page: number) => {
    if (cachedTestSets[page]) {
      setTestSets(cachedTestSets[page]);
      return;
    }

    const offset = (page - 1) * pageSize;
    setIsLoading(true);
    if (intentGroupId) {
      TestSetService.getTestSetsByIntentGroup(
        intentGroupId,
        offset,
        pageSize,
        setIsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          
          setTestSets(response.items);
          setTotalCount(response.total);
          setCachedTestSets(prev => ({
            ...prev,
            [page]: response.items
          }));
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    setCachedTestSets({});
    setCurrentPage(1);
  }, [intentGroupId]);

  useEffect(() => {
    if (intentGroupId) {
      fetchTestSets(currentPage);
    }
  }, [intentGroupId, currentPage, apiKey]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  return (
    <Card className="test-sets-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              {(!intentGroup) ? (
                <Spin size="small" />
              ) : (
                <>
                  <Link
                    to={`/intent-group/${intentGroupId}`}
                    style={{ color: "#828282" }}>
                    {intentGroup.intent_name}
                    {" / "}{" "}
                  </Link>
                  Test Sets
                </>
              )}
            </Typography>
          </Col>
          <Col>
            <PrimaryButton
              title="New Test Set"
              className="buttonWrapper"
              icon={<PlusOutlined />}
              onClick={() => {
                localStorage.removeItem(NEW_TEST_SET_STORAGE_KEY);
                navigate(`/intent-group/${intentGroupId}/testsets/new`);
              }}
            />
          </Col>
        </Row>
        {isLoading ? (
          <Spin
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          />
        ) : (
          <Table
            rowKey={(record) => record.id.toString()}
            columns={testSetColumns}
            dataSource={testSets}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onClick: () => navigate(`/intent-group/${intentGroupId}/testsets/${record.id}`),
              };
            }}
          />
        )}
      </Space>
    </Card>
  );
};

export default TestSets;
